import type { PreComputedFilterOfferIds } from '@cc-shopping/contracts/credit-cards-page/roundup/types';
import type { CreditRangeEnum } from '@cc-shopping/contracts/credit-cards-page/roundup/utils';
import { FilterOffersByType, SortOffersByType } from '../enums';

export function getFilteredIds({
  allOfferIds,
  creditRangeFilter,
  issuerFilter,
  preComputedFilterOfferIds,
  cardFeatureFilter,
}: {
  allOfferIds: string[];
  cardFeatureFilter: FilterOffersByType;
  issuerFilter: string[];
  creditRangeFilter: CreditRangeEnum[];
  preComputedFilterOfferIds: PreComputedFilterOfferIds;
}): string[] {
  let eligibleOfferIds: Set<string>;

  switch (cardFeatureFilter) {
    case FilterOffersByType.ALL_CARDS:
      eligibleOfferIds = preComputedFilterOfferIds.allOfferIds;
      break;
    case FilterOffersByType.HAS_INTRO_OFFER:
      eligibleOfferIds = preComputedFilterOfferIds.introOfferOfferIds;
      break;
    case FilterOffersByType.NO_ANNUAL_FEE:
      eligibleOfferIds = preComputedFilterOfferIds.noAnnualFeeOfferIds;
      break;
  }

  if (issuerFilter.length > 0) {
    const allInstitutionOfferIds = new Set<string>();
    for (const institutionId of issuerFilter) {
      for (const id of preComputedFilterOfferIds.offersByIssuerMap[institutionId] ?? []) {
        allInstitutionOfferIds.add(id);
      }
    }
    eligibleOfferIds = new Set<string>(
      [...eligibleOfferIds].filter(id => allInstitutionOfferIds.has(id)),
    );
  }

  if (creditRangeFilter.length > 0) {
    const allCreditRangeOfferIds = new Set<string>();
    for (const range of creditRangeFilter) {
      for (const id of preComputedFilterOfferIds.offersByRCSMap[range] ?? []) {
        allCreditRangeOfferIds.add(id);
      }
    }
    eligibleOfferIds = new Set<string>(
      [...eligibleOfferIds].filter(id => allCreditRangeOfferIds.has(id)),
    );
  }

  return allOfferIds.filter(id => eligibleOfferIds.has(id));
}

export function getSortedIds({
  filteredIds,
  featuredSequence,
  preComputedFilterOfferIds,
  sortBy,
}: {
  filteredIds: string[];
  featuredSequence: string[];
  preComputedFilterOfferIds: PreComputedFilterOfferIds;
  sortBy: SortOffersByType;
}): string[] {
  let sequence: string[];
  switch (sortBy) {
    case SortOffersByType.FEATURED:
      sequence = featuredSequence;
      break;
    case SortOffersByType.HIGHEST_RATING:
      sequence = preComputedFilterOfferIds.sortedByHighestRating;
      break;
    case SortOffersByType.LOWEST_ANNUAL_FEE:
      sequence = preComputedFilterOfferIds.sortedByLowestAnnualFee;
      break;
    case SortOffersByType.LOWEST_APR:
      sequence = preComputedFilterOfferIds.sortedByLowestAPR;
      break;
  }

  return sortIds(filteredIds, sequence);
}

export function sortIds(ids: string[], sequence: string[]): string[] {
  if (!sequence.length) return ids;

  const orderMapping: Record<string, number> = {};
  sequence.forEach((id, index) => {
    orderMapping[id] = index;
  });

  return [...ids].sort((a, b) => {
    const aIndex = orderMapping[a];
    const bIndex = orderMapping[b];
    if (aIndex !== undefined && bIndex !== undefined) return aIndex - bIndex;
    if (aIndex !== undefined) return -1;
    if (bIndex !== undefined) return 1;
    return 0;
  });
}
